import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import ContactForm from "@/components/ContactForm";
import { 
  Phone, 
  Shield, 
  Clock, 
  DollarSign, 
  Zap, 
  Search,
  Home as HomeIcon,
  Building2,
  CloudLightning,
  Droplets,
  Layers,
  Wrench,
  CheckCircle,
  Star,
  ArrowRight,
  MapPin,
  ClipboardCheck,
  FileText,
  Award
} from "lucide-react";

// Image URLs
const images = {
  hero: "https://images.unsplash.com/photo-1635424825057-7fb6dcd651ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxyb29mZXIlMjByZXBhaXJpbmclMjByb29mJTIwc2FmZXR5JTIwZ2VhcnxlbnwwfHx8fDE3NzI3OTIwNjJ8MA&ixlib=rb-4.1.0&q=85",
  residential: "https://images.unsplash.com/photo-1766392911915-b17454efe76e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGhvdXNlJTIwcm9vZiUyMGV4dGVyaW9yfGVufDB8fHx8MTc3Mjc5MjA2M3ww&ixlib=rb-4.1.0&q=85",
  commercial: "https://images.unsplash.com/photo-1603937124399-a530c64d5f0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwZmxhdCUyMHJvb2YlMjBidWlsZGluZyUyMHNreWxpbmV8ZW58MHx8fHwxNzcyNzkyMDc2fDA&ixlib=rb-4.1.0&q=85",
  stormDamage: "https://images.unsplash.com/photo-1689429028419-b143af3a4116?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxzdG9ybSUyMGRhbWFnZSUyMHJvb2YlMjBzaGluZ2xlcyUyMGxlYWt8ZW58MHx8fHwxNzcyNzkyMDc0fDA&ixlib=rb-4.1.0&q=85",
  teamWork: "https://images.unsplash.com/photo-1681049400158-0ff6249ac315?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxyb29mZXIlMjByZXBhaXJpbmclMjByb29mJTIwc2FmZXR5JTIwZ2VhcnxlbnwwfHx8fDE3NzI3OTIwNjJ8MA&ixlib=rb-4.1.0&q=85",
  worker1: "https://images.unsplash.com/photo-1633759593085-1eaeb724fc88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxyb29mJTIwcmVwYWlyJTIwd29ya2VyJTIwc2hpbmdsZXN8ZW58MHx8fHwxNzcyNzkyMTIxfDA&ixlib=rb-4.1.0&q=85",
  worker2: "https://images.unsplash.com/photo-1726589004565-bedfba94d3a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxyb29mJTIwcmVwYWlyJTIwd29ya2VyJTIwc2hpbmdsZXN8ZW58MHx8fHwxNzcyNzkyMTIxfDA&ixlib=rb-4.1.0&q=85",
  worker3: "https://images.unsplash.com/photo-1635424709845-3a85ad5e1f5e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxyb29mJTIwcmVwYWlyJTIwd29ya2VyJTIwc2hpbmdsZXN8ZW58MHx8fHwxNzcyNzkyMTIxfDA&ixlib=rb-4.1.0&q=85",
  shingles: "https://images.unsplash.com/photo-1553169507-38833977274b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxyb29mJTIwcmVwYWlyJTIwd29ya2VyJTIwc2hpbmdsZXN8ZW58MHx8fHwxNzcyNzkyMTIxfDA&ixlib=rb-4.1.0&q=85",
  tools: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&q=85",
  house: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?crop=entropy&cs=srgb&fm=jpg&q=85",
  inspection: "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?crop=entropy&cs=srgb&fm=jpg&q=85",
};

const services = [
  {
    icon: Droplets,
    title: "Roof Leak Repair",
    description: "Fast identification and repair of leaks to prevent water damage and protect your home's interior.",
  },
  {
    icon: Zap,
    title: "Emergency Roof Repair",
    description: "24/7 emergency response for urgent roof damage. On-site within 60 minutes.",
  },
  {
    icon: CloudLightning,
    title: "Storm Damage Repair",
    description: "Expert repair of wind, hail, and storm damage. Insurance claim assistance included.",
  },
  {
    icon: Layers,
    title: "Roof Replacement",
    description: "Complete roof replacement with premium materials and industry-leading warranties.",
  },
  {
    icon: Wrench,
    title: "Shingle Repair",
    description: "Quick repair and replacement of damaged, missing, or worn shingles.",
  },
  {
    icon: Building2,
    title: "Commercial Roofing",
    description: "Professional roofing solutions for commercial and industrial properties.",
  },
];

const trustPoints = [
  { icon: Shield, title: "Licensed & Insured", description: "Fully licensed, bonded, and insured for your protection." },
  { icon: Clock, title: "24/7 Emergency Service", description: "We're available around the clock for emergencies." },
  { icon: DollarSign, title: "Affordable Pricing", description: "Competitive rates with no hidden fees or surprises." },
  { icon: Zap, title: "Fast Response Time", description: "On-site within 60 minutes for emergency calls." },
  { icon: Search, title: "Free Inspection", description: "Complimentary roof inspection and detailed estimate." },
];

const processSteps = [
  { icon: Search, title: "Inspection", description: "Thorough assessment of your roof's condition" },
  { icon: FileText, title: "Estimate", description: "Detailed quote with transparent pricing" },
  { icon: Wrench, title: "Repair", description: "Expert repair by certified professionals" },
  { icon: ClipboardCheck, title: "Quality Check", description: "Final inspection to ensure perfection" },
];

const testimonials = [
  {
    name: "Michael Thompson",
    location: "Los Angeles, CA",
    rating: 5,
    text: "Cart Genix saved my home during the last storm! They arrived within an hour and fixed the leak that night. Incredible service!",
  },
  {
    name: "Sarah Williams",
    location: "Pasadena, CA",
    rating: 5,
    text: "Professional, affordable, and thorough. The team explained everything clearly and completed the roof replacement ahead of schedule.",
  },
  {
    name: "David Chen",
    location: "Santa Monica, CA",
    rating: 5,
    text: "Best roofing company I've ever worked with. Fair pricing, quality work, and they cleaned up everything when done. Highly recommend!",
  },
  {
    name: "Jennifer Martinez",
    location: "Glendale, CA",
    rating: 5,
    text: "After the windstorm damaged our roof, Cart Genix handled our insurance claim and repairs seamlessly. True professionals!",
  },
];

const serviceAreas = [
  "Los Angeles", "Pasadena", "Santa Monica", "Glendale", "Burbank",
  "Long Beach", "Torrance", "Culver City", "Beverly Hills", "West Hollywood",
  "Malibu", "Manhattan Beach", "Redondo Beach", "Inglewood", "Compton"
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO SECTION */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img 
            src={images.hero} 
            alt="Professional roofer working on roof repair" 
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay"></div>
        </div>
        
        <div className="container-custom relative z-10 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white animate-fadeInUp">
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="trust-badge">
                  <Shield className="w-4 h-4" /> Licensed & Insured
                </span>
                <span className="trust-badge">
                  <Star className="w-4 h-4 text-yellow-400" /> 5-Star Rated
                </span>
              </div>
              
              <h1 className="heading-h1 text-white mb-6">
                EMERGENCY ROOF REPAIR <span className="text-[#F97316]">SERVICES</span> NEAR YOU
              </h1>
              
              <p className="text-body-lg text-slate-300 mb-8 max-w-xl">
                Fast, reliable roof repair in Los Angeles. Leaks stopped. Roofs restored. 
                <span className="text-white font-semibold"> 100% Satisfaction Guaranteed.</span> 
                On-site within 60 minutes.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="btn-primary text-lg" data-testid="hero-cta-inspection">
                    <Search className="w-5 h-5" />
                    Get Free Inspection
                  </Button>
                </Link>
                <a href="tel:+18884451244">
                  <Button className="btn-secondary text-lg" data-testid="hero-cta-call">
                    <Phone className="w-5 h-5" />
                    Call Now: 888-445-1244
                  </Button>
                </a>
              </div>
              
              <div className="flex items-center gap-8 mt-10 pt-10 border-t border-white/20">
                <div>
                  <p className="text-3xl font-bold text-[#F97316]">15+</p>
                  <p className="text-sm text-slate-400">Years Experience</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#F97316]">5000+</p>
                  <p className="text-sm text-slate-400">Roofs Repaired</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#F97316]">24/7</p>
                  <p className="text-sm text-slate-400">Emergency Service</p>
                </div>
              </div>
            </div>
            
            {/* Right Content - Form */}
            <div className="bg-white rounded-lg p-8 shadow-2xl animate-fadeInUp delay-200 hidden lg:block">
              <h3 className="heading-h3 text-slate-900 mb-2">Get Your Free Estimate</h3>
              <p className="text-body mb-6">Fill out the form below and we'll contact you within 24 hours.</p>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section className="section-padding bg-gray" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-16 animate-fadeInUp">
            <span className="text-[#F97316] font-semibold uppercase tracking-wider text-sm">What We Do</span>
            <h2 className="heading-h2 text-slate-900 mt-2">OUR ROOFING SERVICES</h2>
            <p className="text-body max-w-2xl mx-auto mt-4">
              From minor repairs to complete roof replacements, we handle all your roofing needs with expertise and care.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={service.title} 
                className="card-service group animate-fadeInUp"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`service-card-${index}`}
              >
                <div className="service-icon">
                  <service.icon className="w-7 h-7" />
                </div>
                <h3 className="heading-h3 text-slate-900 mb-3">{service.title}</h3>
                <p className="text-body">{service.description}</p>
                <Link 
                  to="/services" 
                  className="inline-flex items-center gap-2 text-[#F97316] font-semibold mt-4 group-hover:gap-3 transition-all"
                >
                  Learn More <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE GALLERY SECTION */}
      <section className="section-padding bg-light" data-testid="gallery-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2 row-span-2 image-hover">
              <img src={images.worker1} alt="Roofer working on roof" className="w-full h-full object-cover rounded-lg" />
            </div>
            <div className="image-hover">
              <img src={images.shingles} alt="Roof shingles" className="w-full h-48 object-cover rounded-lg" />
            </div>
            <div className="image-hover">
              <img src={images.residential} alt="Residential roof" className="w-full h-48 object-cover rounded-lg" />
            </div>
            <div className="image-hover">
              <img src={images.stormDamage} alt="Storm damaged roof" className="w-full h-48 object-cover rounded-lg" />
            </div>
            <div className="image-hover">
              <img src={images.worker2} alt="Roof repair worker" className="w-full h-48 object-cover rounded-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="section-padding bg-dark" data-testid="why-choose-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative animate-slideInLeft">
              <img 
                src={images.teamWork} 
                alt="Professional roofing team at work" 
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#F97316] text-white p-6 rounded-lg shadow-xl hidden md:block">
                <p className="text-4xl font-bold">15+</p>
                <p className="text-sm uppercase tracking-wider">Years of Excellence</p>
              </div>
            </div>
            
            {/* Content */}
            <div>
              <span className="text-[#F97316] font-semibold uppercase tracking-wider text-sm">Why Cart Genix</span>
              <h2 className="heading-h2 text-white mt-2 mb-8">WHY CHOOSE US</h2>
              
              <div className="space-y-6">
                {trustPoints.map((point, index) => (
                  <div 
                    key={point.title} 
                    className="flex gap-4 animate-fadeInUp"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="w-12 h-12 bg-[#F97316] rounded-lg flex items-center justify-center flex-shrink-0">
                      <point.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">{point.title}</h3>
                      <p className="text-slate-400">{point.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/about">
                  <Button className="btn-primary" data-testid="why-cta-about">
                    Learn More About Us
                  </Button>
                </Link>
                <a href="tel:+18884451244">
                  <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-slate-900 font-semibold h-12 px-8 rounded-md transition-all">
                    <Phone className="w-4 h-4" />
                    Call: 888-445-1244
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="section-padding bg-gray" data-testid="process-section">
        <div className="container-custom">
          <div className="text-center mb-16 animate-fadeInUp">
            <span className="text-[#F97316] font-semibold uppercase tracking-wider text-sm">How It Works</span>
            <h2 className="heading-h2 text-slate-900 mt-2">OUR SIMPLE PROCESS</h2>
            <p className="text-body max-w-2xl mx-auto mt-4">
              We make roof repair easy with our straightforward 4-step process.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div 
                key={step.title} 
                className="process-step animate-fadeInUp"
                style={{ animationDelay: `${index * 150}ms` }}
                data-testid={`process-step-${index}`}
              >
                <div className="w-16 h-16 bg-[#F97316] rounded-full flex items-center justify-center text-white mb-6 relative z-10">
                  <step.icon className="w-8 h-8" />
                </div>
                <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-slate-600 font-bold text-sm mb-4">
                  {index + 1}
                </div>
                <h3 className="heading-h3 text-slate-900 mb-2">{step.title}</h3>
                <p className="text-body">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MORE IMAGES SECTION */}
      <section className="py-8 bg-light">
        <div className="container-custom">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            <div className="image-hover">
              <img src={images.worker3} alt="Roofing work" className="w-full h-32 object-cover rounded-lg" />
            </div>
            <div className="image-hover">
              <img src={images.commercial} alt="Commercial roof" className="w-full h-32 object-cover rounded-lg" />
            </div>
            <div className="image-hover">
              <img src={images.hero} alt="Roof repair" className="w-full h-32 object-cover rounded-lg" />
            </div>
            <div className="image-hover hidden md:block">
              <img src={images.residential} alt="Residential roofing" className="w-full h-32 object-cover rounded-lg" />
            </div>
            <div className="image-hover hidden md:block">
              <img src={images.teamWork} alt="Team work" className="w-full h-32 object-cover rounded-lg" />
            </div>
            <div className="image-hover hidden md:block">
              <img src={images.stormDamage} alt="Storm damage" className="w-full h-32 object-cover rounded-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="section-padding bg-light" data-testid="testimonials-section">
        <div className="container-custom">
          <div className="text-center mb-16 animate-fadeInUp">
            <span className="text-[#F97316] font-semibold uppercase tracking-wider text-sm">Testimonials</span>
            <h2 className="heading-h2 text-slate-900 mt-2">WHAT OUR CLIENTS SAY</h2>
            <p className="text-body max-w-2xl mx-auto mt-4">
              Don't just take our word for it. Here's what our satisfied customers have to say.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.name} 
                className="card-testimonial animate-fadeInUp"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6 not-italic">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#0F172A] rounded-full flex items-center justify-center text-white font-bold">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS SECTION */}
      <section className="section-padding bg-gray" data-testid="service-areas-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fadeInUp">
              <span className="text-[#F97316] font-semibold uppercase tracking-wider text-sm">Coverage</span>
              <h2 className="heading-h2 text-slate-900 mt-2 mb-6">AREAS WE SERVE</h2>
              <p className="text-body mb-8">
                Cart Genix proudly serves Los Angeles and surrounding communities. 
                No matter where you are in the greater LA area, our expert roofing team is just a call away.
              </p>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {serviceAreas.map((area) => (
                  <div key={area} className="flex items-center gap-2 text-slate-700">
                    <MapPin className="w-4 h-4 text-[#F97316]" />
                    <span>{area}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8">
                <Link to="/service-areas">
                  <Button className="btn-primary" data-testid="service-areas-cta">
                    View All Service Areas
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative animate-fadeInUp delay-200">
              <img 
                src={images.residential} 
                alt="Residential roof in Los Angeles area" 
                className="rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-4 -left-4 bg-white p-4 rounded-lg shadow-lg">
                <div className="flex items-center gap-3">
                  <Award className="w-10 h-10 text-[#F97316]" />
                  <div>
                    <p className="font-bold text-slate-900">Serving LA Since 2009</p>
                    <p className="text-sm text-slate-500">15+ Years of Excellence</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA / CONTACT SECTION */}
      <section className="section-padding bg-dark" data-testid="contact-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left - CTA */}
            <div className="animate-fadeInUp">
              <span className="text-[#F97316] font-semibold uppercase tracking-wider text-sm">Get Started</span>
              <h2 className="heading-h2 text-white mt-2 mb-6">READY TO FIX YOUR ROOF?</h2>
              <p className="text-slate-300 text-lg mb-8">
                Don't wait until a small leak becomes a big problem. Contact us today for a free inspection 
                and estimate. Our expert team is standing by to help.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <CheckCircle className="w-6 h-6 text-[#F97316]" />
                  <span className="text-white">Free roof inspection and estimate</span>
                </div>
                <div className="flex items-center gap-4">
                  <CheckCircle className="w-6 h-6 text-[#F97316]" />
                  <span className="text-white">Same-day emergency service available</span>
                </div>
                <div className="flex items-center gap-4">
                  <CheckCircle className="w-6 h-6 text-[#F97316]" />
                  <span className="text-white">Financing options available</span>
                </div>
              </div>
              
              <div className="bg-slate-800 p-6 rounded-lg">
                <p className="text-slate-400 mb-2">Call us directly at:</p>
                <a href="tel:+18884451244" className="text-3xl font-bold text-white hover:text-[#F97316] transition-colors">
                  +1-888-445-1244
                </a>
                <p className="text-slate-400 mt-2">Available 24/7 for emergencies</p>
              </div>
            </div>
            
            {/* Right - Form */}
            <div className="bg-white rounded-lg p-8 shadow-2xl animate-fadeInUp delay-200" data-testid="contact-form-section">
              <h3 className="heading-h3 text-slate-900 mb-2">Request Your Free Estimate</h3>
              <p className="text-body mb-6">Fill out the form and we'll contact you within 24 hours.</p>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
